// tslint:disable:no-submodule-imports
import { ResponseParsingError, ServiceError, ServiceTimeoutError, Services } from '@volkswagen-onehub/library-services-common';
import { fetchDataversion as getTenantDataVersion } from '@volkswagen-onehub/library-services-dataversion';
import { fetchStartConfiguration } from '@volkswagen-onehub/library-services-start-configuration';
import { BuildabilityStatusV2 as BuildabilityStatus } from './types';
import { searchCarlineId, searchExteriorId, searchInteriorId, searchIsConflicted, searchModelId, searchModelVersion, searchModelYear, searchOptionIds, searchSalesGroupId, searchTrimName } from './buildability/queries';
import { isCompleteConfiguration } from './configuration-utils';
import { isCarlineConfiguration } from './validator';
let dataversions = {};
export function resetDataversions() {
    dataversions = {};
}
export function setDataversion(tenant, newDataversion) {
    dataversions[tenant] = newDataversion;
}
export function isErrorResponse(response) {
    return response && response.hasOwnProperty('error');
}
export async function createBuildableConfiguration(serviceConfigProvider, language, timeout, configuration) {
    try {
        const data = await fetchBuildableConfigurationData(serviceConfigProvider, language, timeout, configuration);
        const buildableConfiguration = getBuildableConfigurationFromData(configuration.category, data);
        if (!buildableConfiguration) {
            return {
                error: 'IMPOSSIBLE'
            };
        }
        return {
            configuration: buildableConfiguration
        };
    }
    catch (error) {
        // TODO NGWD6-335: implement timeout + service + response-parse error
        let responseErrorDescription = error.message;
        if (error instanceof ServiceTimeoutError) {
            return {
                error: 'TIMEOUT'
            };
        }
        if (error instanceof ServiceError) {
            if (error.service === Services.VICCI_VCS_TENANT_DATA_VERSION) {
                responseErrorDescription = `Could not get dataversion. Service returned an error: ${error.message}`;
            }
            else if (!(error instanceof ResponseParsingError)) {
                return {
                    error: 'SERVICE_ERROR',
                    statusCode: error.status
                };
            }
        }
        return {
            error: 'RESPONSE_ERROR',
            description: responseErrorDescription,
            _wrappedError: error
        };
    }
}
// tslint:disable-next-line:no-empty
function expectNever(_param) { }
async function fetchBuildableConfigurationData(serviceConfigProvider, language, timeout, configuration) {
    const sharedServiceConfig = serviceConfigProvider.configs.vicci;
    if (!sharedServiceConfig) {
        throw new Error('No service configuration for service "vicci" provided');
    }
    if (!isCarlineConfiguration(configuration)) {
        throw new Error('No category or carline id is provided in the configuration');
    }
    let tenant;
    switch (configuration.category) {
        case 'commercial':
            tenant = sharedServiceConfig.tenantCommercial;
            break;
        case 'private':
            tenant = sharedServiceConfig.tenantPrivate;
            break;
        default:
            expectNever(configuration.category);
    }
    if (!tenant) {
        throw new Error(`Cannot find tenant for category "${configuration.category}"`);
    }
    const sharedServiceCustomConfig = sharedServiceConfig.customConfig;
    const serviceTimeout = typeof timeout === 'number'
        ? timeout
        : sharedServiceCustomConfig &&
            sharedServiceCustomConfig.vcs &&
            sharedServiceCustomConfig.vcs.timeout;
    const serviceConfig = {
        name: 'vicci',
        origin: sharedServiceConfig.urlOrigin,
        path: `${sharedServiceConfig.urlPath || ''}/vcs`,
        authentication: sharedServiceConfig.credentials,
        timeout: serviceTimeout
    };
    tenant = tenant.replace('{language}', language);
    const currentDataversion = await fetchDataversion(serviceConfig, tenant);
    try {
        const response = await fetchStartConfiguration(serviceConfig, {}, // mocks are not supported right now
        currentDataversion, tenant, configuration, {
            enforceBuildable: true,
            fetchMandatory: false,
            fetchPrices: false,
            fetchInvisible: false,
            fetchPackageContents: false,
            fetchFeatureGroups: false,
            fetchExteriors: false,
            // we need to set fetchInterior to true, otherwise we don't get any modelFilters in the response, which we use to extract the trim.
            fetchInteriors: true,
            fetchFeatures: false,
            fetchMediaForConfiguration: false,
            fetchMediaForFeatures: false,
            fetchMarketableLevels: false
        });
        return response;
    }
    catch (error) {
        // the initial configuration did not exist (e.g. carline)
        if (error instanceof ResponseParsingError && error.status === 204) {
            return {};
        }
        // this engine given in the intial configuration does not exist
        if (error instanceof ServiceError &&
            error.status === 500 &&
            error.response) {
            const jsonResponse = await error.response.json();
            if (jsonResponse.code === 4005201) {
                return {};
            }
        }
        throw error;
    }
}
async function fetchDataversion(serviceConfig, tenant) {
    const existingDataVersion = dataversions[tenant];
    if (existingDataVersion) {
        return existingDataVersion;
    }
    const dataversionResponse = await getTenantDataVersion(serviceConfig, {}, tenant);
    const newDataversion = dataversionResponse && dataversionResponse.tenantDataVersion;
    if (!newDataversion) {
        throw new Error('Could not get dataversion for "vicci" service');
    }
    dataversions[tenant] = newDataversion;
    return newDataversion;
}
function getBuildableConfigurationFromData(category, data) {
    const conflict = searchIsConflicted(data);
    if (conflict) {
        // if VICCI returns a conflict, it is impossible to make the configuration buildable
        return null;
    }
    const configuration = {
        category,
        carlineId: searchCarlineId(data),
        exteriorId: searchExteriorId(data),
        options: searchOptionIds(data),
        interiorId: searchInteriorId(data),
        modelId: searchModelId(data),
        modelVersion: searchModelVersion(data),
        modelYear: searchModelYear(data).slice(0, 4),
        salesGroupId: searchSalesGroupId(data),
        trimName: searchTrimName(data),
        buildabilityStatus: BuildabilityStatus.BUILDABLE
    };
    if (!isCompleteConfiguration(configuration)) {
        return null;
    }
    return configuration;
}
